import * as React from "react";
import {
  Alert,
  AlertTitle,
  Box,
  Card,
  CardContent,
  CardHeader,
  Divider,
  Grid,
  IconButton,
  LinearProgress,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import { useTitle } from "../../hooks/useTitle";
import { Controller, useForm } from "react-hook-form";
import useScroll from "../../hooks/useScroll";
import SelectSkills from "./SelectSkills";
import SelectLanguages from "./SelectLanguages";
import { LoadingButton } from "@mui/lab";
import { toast } from "react-toastify";
import SelectCountry from "../../components/SelectCountry";
import UploadPhoto from "./UploadPhoto";
import AddIcon from "@mui/icons-material/Add";
import ReactStickyBox from "react-sticky-box";
import { getOriginalLastName, getPrivateCompanyName } from "../../utils/helper";
import SelectWorkStatus from "./SelectWorkStatus";
import SelectAvailability from "./SelectAvailability";
import SubtitlesOutlined from "@mui/icons-material/SubtitlesOutlined";
import SchoolOutlined from "@mui/icons-material/SchoolOutlined";
import DeleteOutline from "@mui/icons-material/DeleteOutline";
import { UserCertification } from "../../types/certification";
import ConfirmDeleteModal from "../../components/ConfirmDeleteModal";
import ModalUserCertificationForm from "./ModalUserCertificationForm";
import ModalUserEducationForm from "./ModalUserEducationForm";
import { UserEducation } from "../../types/education";
import { EditOutlined } from "@mui/icons-material";
import SelectVerticals from "../../components/SelectVerticals";
import {
  useChangePhotoMutation,
  useDeleteUserCertificationMutation,
  useDeleteUserEducationMutation,
  useDeleteUserWorkExperienceMutation,
  useUpdateUserLanguagesMutation,
  useUpdateUserMutation,
  useUpdateUserSkillsMutation,
} from "../../services/user.service";
import { useCurrentUser } from "../../hooks/useCurrentUser";
import { useGetUserQuery } from "../../services/auth.service";
import ModalUserExperienceForm from "./ModalUserExperienceForm";
import { UserWorkExperience } from "../../types/workExperience";
import SelectState from "../../components/SelectState";

const EditAccountPage = () => {
  const user = useCurrentUser();
  const navigate = useNavigate();
  const [scrollToLogin, loginRef] = useScroll();
  const [scrollToBasic, basicRef] = useScroll();
  const [scrollToSetting, settingRef] = useScroll();
  const [scrollToSkill, skillRef] = useScroll();
  const [scrollToLanguage, languageRef] = useScroll();
  const [scrollToWorkStatus, workStatusRef] = useScroll();
  const [scrollToAvail, availRef] = useScroll();
  const [scrollToCert, certRef] = useScroll();
  const [scrollToEdu, eduRef] = useScroll();
  const [scrollToExp, expRef] = useScroll();

  useTitle("Edit Account");

  const { data, isLoading, isFetching, isError, error } = useGetUserQuery();
  const [edit, { isLoading: updating }] = useUpdateUserMutation();
  const [updateSkills, { isLoading: updatingSkills }] =
    useUpdateUserSkillsMutation();
  const [updateLanguages, { isLoading: updatingLanguages }] =
    useUpdateUserLanguagesMutation();
  const [changePhoto, { isLoading: isUploadingPhoto }] =
    useChangePhotoMutation();

  const [doDeleteCert] = useDeleteUserCertificationMutation();
  const [doDeleteEdu] = useDeleteUserEducationMutation();
  const [doDeleteExp] = useDeleteUserWorkExperienceMutation();

  const [certModal, setCertModal] = React.useState<{
    open: boolean;
    data: UserCertification | null;
  }>({
    open: false,
    data: null,
  });
  const [deleteCertModal, setConfirmDeleteCertModal] = React.useState<{
    open: boolean;
    id: number;
  }>({
    open: false,
    id: 0,
  });
  const closeCertDeleteModal = () =>
    setConfirmDeleteCertModal({ ...deleteCertModal, open: false });
  const handleCertDelete = async () => {
    if (deleteCertModal.id > 0) {
      try {
        await doDeleteCert(deleteCertModal.id).unwrap();
        closeCertDeleteModal();
        toast.success("Successfully deleted");
      } catch (err) {
        closeCertDeleteModal();
      }
    }
  };

  const [eduModal, setEduModal] = React.useState<{
    open: boolean;
    data: UserEducation | null;
  }>({
    open: false,
    data: null,
  });
  const [deleteEduModal, setConfirmDeleteEduModal] = React.useState<{
    open: boolean;
    id: number;
  }>({
    open: false,
    id: 0,
  });
  const closeEduDeleteModal = () =>
    setConfirmDeleteEduModal({ ...deleteEduModal, open: false });
  const handleEduDelete = async () => {
    if (deleteEduModal.id > 0) {
      try {
        await doDeleteEdu(deleteEduModal.id).unwrap();
        closeEduDeleteModal();
        toast.success("Successfully deleted");
      } catch (err) {
        closeEduDeleteModal();
      }
    }
  };

  const [expModal, setExpModal] = React.useState<{
    open: boolean;
    data: UserWorkExperience | null;
  }>({
    open: false,
    data: null,
  });
  const [deleteExpModal, setConfirmDeleteExpModal] = React.useState<{
    open: boolean;
    id: number;
  }>({
    open: false,
    id: 0,
  });
  const closeExpDeleteModal = () =>
    setConfirmDeleteExpModal({ ...deleteExpModal, open: false });
  const handleExpDelete = async () => {
    if (deleteExpModal.id > 0) {
      try {
        await doDeleteExp(deleteExpModal.id).unwrap();
        closeExpDeleteModal();
        toast.success("Successfully deleted");
      } catch (err) {
        closeExpDeleteModal();
      }
    }
  };

  const {
    handleSubmit,
    control,
    setValue,
    formState: { errors },
  } = useForm();

  const onSubmit = async (payload: any) => {
    try {
      if (!payload.password) delete payload.password;
      if (payload.company !== "") {
        payload.last_name = payload.last_name.trim() + ` [${payload.company}]`;
      }
      payload.id = data?.id;

      payload.avail_monday = payload.workAvails.includes("avail_monday")
        ? 1
        : 0;
      payload.avail_tuesday = payload.workAvails.includes("avail_tuesday")
        ? 1
        : 0;
      payload.avail_wednesday = payload.workAvails.includes("avail_wednesday")
        ? 1
        : 0;
      payload.avail_thursday = payload.workAvails.includes("avail_thursday")
        ? 1
        : 0;
      payload.avail_friday = payload.workAvails.includes("avail_friday")
        ? 1
        : 0;
      payload.avail_saturday = payload.workAvails.includes("avail_saturday")
        ? 1
        : 0;
      payload.avail_sunday = payload.workAvails.includes("avail_sunday")
        ? 1
        : 0;
      delete payload.workAvails;

      if (payload.photo instanceof File) await changePhoto(payload.photo);
      await updateSkills(payload.skills);
      await updateLanguages(payload.languages);
      await edit(payload).unwrap();
      toast.success("Account updated");

      navigate(`/account`);
    } catch (err) {}
  };

  React.useEffect(() => {
    setValue("email", data?.email || "");
    setValue("password", "");
    // setValue("security_questions", data?.security_questions || "");
    // setValue("security_answer", data?.security_answer || "");
    setValue("first_name", data?.first_name || "");
    setValue("last_name", getOriginalLastName(data?.last_name) || "");
    setValue("phone_number", data?.phone_number || "");
    setValue("physical_address_1", data?.physical_address_1 || "");
    setValue("physical_address_2", data?.physical_address_2 || "");
    setValue("country", data?.country_id || 0);
    setValue("city", data?.city || "");
    setValue("state", data?.state || "");
    setValue("zip", data?.zip || "");
    setValue("linkedin", data?.linkedin || "");
    setValue("facebook", data?.facebook || "");
    setValue("instagram", data?.instagram || "");
    setValue("website", data?.website || "");
    setValue("bio", data?.bio || "");
    setValue("min_pay", data?.min_pay || "");
    setValue("max_pay", data?.max_pay || "");
    setValue("commute_radius", data?.commute_radius || "");
    const skillIds = data?.skills?.map((it) => it.id);
    setValue("skills", skillIds || []);
    const verticalIds = data?.business_verticals?.map((it) => it.id);
    setValue("business_verticals", verticalIds || []);
    const languageIds = data?.languages?.map((it) => it.id);
    setValue("languages", languageIds || []);
    setValue("photo", data?.photo[0]?.original_url);
    setValue("company", getPrivateCompanyName(data?.last_name) || "");
    setValue("work_status", data?.talent?.work_status || "");
    setValue("is_valid_us", data?.talent?.is_valid_us || "");

    const avails = [];
    if (!!data?.talent?.avail_monday) avails.push("avail_monday");
    if (!!data?.talent?.avail_monday) avails.push("avail_tuesday");
    if (!!data?.talent?.avail_sunday) avails.push("avail_sunday");
    if (!!data?.talent?.avail_wednesday) avails.push("avail_wednesday");
    if (!!data?.talent?.avail_thursday) avails.push("avail_thursday");
    if (!!data?.talent?.avail_friday) avails.push("avail_friday");
    if (!!data?.talent?.avail_saturday) avails.push("avail_saturday");
    if (!!data?.talent?.avail_sunday) avails.push("avail_sunday");
    setValue("workAvails", avails);
  }, [data]);

  //@ts-ignore
  if (isError && error?.status == 404) {
    return (
      <Box>
        <Alert severity="error">
          <AlertTitle>ERROR</AlertTitle>
          Independent Professional not found or deleted
        </Alert>
      </Box>
    );
  }

  return (
    <Box>
      {isFetching && <LinearProgress />}
      <Grid container spacing={1}>
        <Grid item xs={12} lg={3} sx={{ display: { xs: "none", lg: "block" } }}>
          <ReactStickyBox offsetTop={70} offsetBottom={20}>
            <List>
              <ListItem>
                <ListItemButton onClick={scrollToLogin as any}>
                  <ListItemText>Login</ListItemText>
                </ListItemButton>
              </ListItem>
              <ListItem>
                <ListItemButton onClick={scrollToBasic as any}>
                  <ListItemText>Basic Profile</ListItemText>
                </ListItemButton>
              </ListItem>
              <ListItem>
                <ListItemButton onClick={scrollToSetting as any}>
                  <ListItemText>Worker Settings</ListItemText>
                </ListItemButton>
              </ListItem>
              <ListItem>
                <ListItemButton onClick={scrollToSkill as any}>
                  <ListItemText>Skills</ListItemText>
                </ListItemButton>
              </ListItem>
              <ListItem>
                <ListItemButton onClick={scrollToLanguage as any}>
                  <ListItemText>Languages</ListItemText>
                </ListItemButton>
              </ListItem>
              <ListItem>
                <ListItemButton onClick={scrollToWorkStatus as any}>
                  <ListItemText>Work Status</ListItemText>
                </ListItemButton>
              </ListItem>
              <ListItem>
                <ListItemButton onClick={scrollToWorkStatus as any}>
                  <ListItemText>Availability</ListItemText>
                </ListItemButton>
              </ListItem>
              <ListItem>
                <ListItemButton onClick={scrollToCert as any}>
                  <ListItemText>Certificates & Licenses</ListItemText>
                </ListItemButton>
              </ListItem>
              <ListItem>
                <ListItemButton onClick={scrollToEdu as any}>
                  <ListItemText>Educations</ListItemText>
                </ListItemButton>
              </ListItem>
              <ListItem>
                <ListItemButton onClick={scrollToExp as any}>
                  <ListItemText>Work Experiences</ListItemText>
                </ListItemButton>
              </ListItem>
            </List>
          </ReactStickyBox>
        </Grid>
        <Grid item sm={12} lg={9}>
          <form onSubmit={handleSubmit(onSubmit)}>
            <Box>
              <Card ref={loginRef}>
                <CardHeader title="Login" />
                <CardContent>
                  <Stack direction="row" spacing={1}>
                    <Controller
                      name="email"
                      control={control}
                      rules={{ required: true }}
                      render={({ field: { onChange, onBlur, value, ref } }) => (
                        <TextField
                          InputLabelProps={{ shrink: !!value }}
                          label="Email"
                          value={value}
                          fullWidth
                          onChange={onChange}
                          onBlur={onBlur}
                          disabled={true}
                          sx={{ mt: 0 }}
                          error={!!errors.email}
                          helperText={
                            !!errors.email && "This field is required"
                          }
                        />
                      )}
                    />
                    <Controller
                      name="password"
                      control={control}
                      rules={{ required: !!data ? false : true }}
                      render={({ field: { onChange, onBlur, value, ref } }) => (
                        <>
                          <TextField
                            InputLabelProps={{ shrink: !!value }}
                            label="Password"
                            value={value}
                            fullWidth
                            onChange={onChange}
                            onBlur={onBlur}
                            sx={{ mt: 0 }}
                            error={!!errors.password}
                            helperText={
                              !data
                                ? !!errors.password && "This field is required"
                                : "Leave blank if you don't want to change the password"
                            }
                            type="new-password"
                          />
                        </>
                      )}
                    />
                  </Stack>
                </CardContent>
              </Card>
              <Card sx={{ mt: 1 }} ref={basicRef}>
                <CardHeader title="Basic Profile" />
                <CardContent>
                  <Controller
                    name="photo"
                    control={control}
                    render={({ field: { onChange, value } }) => (
                      <UploadPhoto onChange={onChange} value={value} />
                    )}
                  />
                  <Stack direction="row" spacing={1} sx={{ mt: 3 }}>
                    <Controller
                      name="first_name"
                      control={control}
                      rules={{ required: true }}
                      render={({ field: { onChange, onBlur, value, ref } }) => (
                        <TextField
                          InputLabelProps={{ shrink: !!value }}
                          label="First name"
                          value={value}
                          fullWidth
                          onChange={onChange}
                          onBlur={onBlur}
                          sx={{ mt: 0 }}
                          error={!!errors.first_name}
                          helperText={
                            !!errors.first_name && "This field is required"
                          }
                        />
                      )}
                    />
                    <Controller
                      name="last_name"
                      control={control}
                      rules={{ required: true }}
                      render={({ field: { onChange, onBlur, value, ref } }) => (
                        <TextField
                          InputLabelProps={{ shrink: !!value }}
                          label="Last name"
                          value={value}
                          fullWidth
                          onChange={onChange}
                          onBlur={onBlur}
                          error={!!errors.last_name}
                          helperText={
                            !!errors.last_name && "This field is required"
                          }
                        />
                      )}
                    />
                  </Stack>
                  <Controller
                    name="phone_number"
                    control={control}
                    rules={{ required: true }}
                    render={({ field: { onChange, onBlur, value, ref } }) => (
                      <TextField
                        InputLabelProps={{ shrink: !!value }}
                        label="Phone number"
                        value={value}
                        fullWidth
                        onChange={onChange}
                        onBlur={onBlur}
                        error={!!errors.phone_number}
                        helperText={
                          !!errors.phone_number && "This field is required"
                        }
                      />
                    )}
                  />
                  <Divider sx={{ mt: 1 }}>
                    <Typography variant="body2">Address</Typography>
                  </Divider>
                  <Controller
                    name="physical_address_1"
                    control={control}
                    rules={{ required: true }}
                    render={({ field: { onChange, onBlur, value, ref } }) => (
                      <TextField
                        InputLabelProps={{ shrink: !!value }}
                        label="Physical Address 1"
                        value={value}
                        fullWidth
                        onChange={onChange}
                        onBlur={onBlur}
                        error={!!errors.physical_address_1}
                        helperText={
                          !!errors.physical_address_1 &&
                          "This field is required"
                        }
                      />
                    )}
                  />
                  <Controller
                    name="physical_address_2"
                    control={control}
                    render={({ field: { onChange, onBlur, value, ref } }) => (
                      <TextField
                        InputLabelProps={{ shrink: !!value }}
                        label="Physical Address 2 (optional)"
                        value={value}
                        fullWidth
                        onChange={onChange}
                        onBlur={onBlur}
                      />
                    )}
                  />
                  <Stack direction={"row"} spacing={1} sx={{ mt: 1 }}>
                    <Controller
                      name="country"
                      control={control}
                      render={({ field: { onChange, value } }) => (
                        <SelectCountry
                          onChange={onChange}
                          value={value}
                          error={!!errors.country}
                          helperText={
                            !!errors.country && "This field is required"
                          }
                        />
                      )}
                    />
                    <Controller
                      name="state"
                      control={control}
                      rules={{ required: true }}
                      render={({ field: { onChange, onBlur, value, ref } }) => (
                        <SelectState
                          onChange={onChange}
                          value={value}
                          error={!!errors.state}
                          helperText={
                            !!errors.state && "This field is required"
                          }
                        />
                      )}
                    />
                  </Stack>
                  <Stack direction={"row"} spacing={1} sx={{ mt: 2 }}>
                    <Controller
                      name="city"
                      control={control}
                      rules={{ required: true }}
                      render={({ field: { onChange, onBlur, value, ref } }) => (
                        <TextField
                          InputLabelProps={{ shrink: !!value }}
                          label="City"
                          value={value}
                          fullWidth
                          onChange={onChange}
                          onBlur={onBlur}
                          sx={{ mt: 0 }}
                          error={!!errors.city}
                          helperText={!!errors.city && "This field is required"}
                        />
                      )}
                    />
                    <Controller
                      name="zip"
                      control={control}
                      rules={{ required: true }}
                      render={({ field: { onChange, onBlur, value, ref } }) => (
                        <TextField
                          InputLabelProps={{ shrink: !!value }}
                          label="Zip"
                          value={value}
                          fullWidth
                          onChange={onChange}
                          onBlur={onBlur}
                          error={!!errors.zip}
                          helperText={!!errors.zip && "This field is required"}
                        />
                      )}
                    />
                  </Stack>
                  <Divider sx={{ mt: 2 }}>
                    <Typography variant="body2">Social Medias</Typography>
                  </Divider>
                  <Controller
                    name="linkedin"
                    control={control}
                    render={({ field: { onChange, onBlur, value, ref } }) => (
                      <TextField
                        InputLabelProps={{ shrink: !!value }}
                        label="LinkedIn URL (optional)"
                        value={value}
                        fullWidth
                        onChange={onChange}
                        onBlur={onBlur}
                      />
                    )}
                  />
                  <Controller
                    name="facebook"
                    control={control}
                    render={({ field: { onChange, onBlur, value, ref } }) => (
                      <TextField
                        InputLabelProps={{ shrink: !!value }}
                        label="Facebook URL (optional)"
                        value={value}
                        fullWidth
                        onChange={onChange}
                        onBlur={onBlur}
                      />
                    )}
                  />
                  <Controller
                    name="instagram"
                    control={control}
                    render={({ field: { onChange, onBlur, value, ref } }) => (
                      <TextField
                        InputLabelProps={{ shrink: !!value }}
                        label="Instagram URL (optional)"
                        value={value}
                        fullWidth
                        onChange={onChange}
                        onBlur={onBlur}
                      />
                    )}
                  />
                  <Controller
                    name="website"
                    control={control}
                    render={({ field: { onChange, onBlur, value, ref } }) => (
                      <TextField
                        InputLabelProps={{ shrink: !!value }}
                        label="Website URL (optional)"
                        value={value}
                        fullWidth
                        onChange={onChange}
                        onBlur={onBlur}
                      />
                    )}
                  />
                </CardContent>
              </Card>
              <Card sx={{ mt: 1 }} ref={settingRef}>
                <CardHeader title="Work Settings" />
                <CardContent>
                  <Controller
                    name="bio"
                    control={control}
                    render={({ field: { onChange, onBlur, value, ref } }) => (
                      <TextField
                        InputLabelProps={{ shrink: !!value }}
                        label="Tell us about you (optional)"
                        value={value}
                        fullWidth
                        onChange={onChange}
                        onBlur={onBlur}
                        multiline
                        rows={6}
                        sx={{ my: 0 }}
                      />
                    )}
                  />
                  <Stack direction="row" spacing={1} sx={{ mt: 3 }}>
                    <Controller
                      name="min_pay"
                      control={control}
                      rules={{ required: true }}
                      render={({ field: { onChange, onBlur, value, ref } }) => (
                        <TextField
                          InputLabelProps={{ shrink: !!value }}
                          label="Minimum Pay ($)"
                          value={value}
                          fullWidth
                          onChange={onChange}
                          onBlur={onBlur}
                          sx={{ mt: 0 }}
                          type="number"
                          error={!!errors.min_pay}
                          helperText={
                            !!errors.min_pay && "This field is required"
                          }
                        />
                      )}
                    />
                    <Controller
                      name="max_pay"
                      control={control}
                      rules={{ required: true }}
                      render={({ field: { onChange, onBlur, value, ref } }) => (
                        <TextField
                          InputLabelProps={{ shrink: !!value }}
                          label="Maximum Pay ($)"
                          value={value}
                          fullWidth
                          onChange={onChange}
                          onBlur={onBlur}
                          type="number"
                          error={!!errors.max_pay}
                          helperText={
                            !!errors.max_pay && "This field is required"
                          }
                        />
                      )}
                    />
                  </Stack>
                  <Controller
                    name="commute_radius"
                    control={control}
                    rules={{ required: true }}
                    render={({ field: { onChange, onBlur, value, ref } }) => (
                      <TextField
                        InputLabelProps={{ shrink: !!value }}
                        label="Commute Radius (miles)"
                        value={value}
                        fullWidth
                        onChange={onChange}
                        onBlur={onBlur}
                        type="number"
                        error={!!errors.commute_radius}
                        helperText={
                          !!errors.commute_radius && "This field is required"
                        }
                      />
                    )}
                  />
                </CardContent>
              </Card>
              <Card sx={{ mt: 1 }} ref={skillRef}>
                <CardHeader title="Skills" />
                <CardContent>
                  <Controller
                    name="skills"
                    control={control}
                    render={({ field: { onChange, value } }) => (
                      <SelectSkills onChange={onChange} values={value} />
                    )}
                  />
                </CardContent>
              </Card>
              <Card sx={{ mt: 1 }} ref={skillRef}>
                <CardHeader title="Industries" />
                <CardContent>
                  <Controller
                    name="business_verticals"
                    control={control}
                    render={({ field: { onChange, value } }) => (
                      <SelectVerticals onChange={onChange} values={value} />
                    )}
                  />
                </CardContent>
              </Card>
              <Card sx={{ mt: 1 }} ref={languageRef}>
                <CardHeader title="Languages" />
                <CardContent>
                  <Controller
                    name="languages"
                    control={control}
                    render={({ field: { onChange, value } }) => (
                      <SelectLanguages onChange={onChange} values={value} />
                    )}
                  />
                </CardContent>
              </Card>
              <Card sx={{ mt: 1 }} ref={workStatusRef}>
                <CardHeader title="Work Status & Availability" />
                <CardContent>
                  <Controller
                    name="work_status"
                    control={control}
                    rules={{ required: true }}
                    render={({ field: { onChange, value } }) => (
                      <SelectWorkStatus
                        onChange={onChange}
                        value={value}
                        error={!!errors.work_status}
                        helperText={
                          !!errors.work_status && "This field is required"
                        }
                      />
                    )}
                  />
                  <Controller
                    name="workAvails"
                    control={control}
                    render={({ field: { onChange, value } }) => (
                      <SelectAvailability onChange={onChange} values={value} />
                    )}
                  />
                </CardContent>
              </Card>
              <Card sx={{ mt: 1 }} ref={certRef}>
                <CardHeader
                  title="Certificates & Licenses"
                  action={
                    <IconButton
                      onClick={() => setCertModal({ open: true, data: null })}
                    >
                      <AddIcon />
                    </IconButton>
                  }
                />
                <CardContent>
                  {data?.certifications?.length == 0 ? (
                    <Typography
                      variant="body2"
                      color="#aaa"
                      sx={{ fontStyle: "italic" }}
                    >
                      No certification added
                    </Typography>
                  ) : (
                    data?.certifications?.map((it) => (
                      <ListItem
                        key={it.id}
                        secondaryAction={
                          <Stack direction={"row"}>
                            <IconButton
                              edge="end"
                              size="small"
                              onClick={() =>
                                setCertModal({
                                  open: true,
                                  data: it,
                                })
                              }
                            >
                              <EditOutlined fontSize="small" />
                            </IconButton>
                            <IconButton
                              edge="end"
                              size="small"
                              color="error"
                              onClick={() =>
                                setConfirmDeleteCertModal({
                                  open: true,
                                  id: it.id,
                                })
                              }
                            >
                              <DeleteOutline fontSize="small" />
                            </IconButton>
                          </Stack>
                        }
                      >
                        <ListItemIcon>
                          <SubtitlesOutlined />
                        </ListItemIcon>
                        <ListItemText
                          primary={`${it.certification?.name} (${it.certification?.provider})`}
                          secondary={`Issued: ${it.issue_date}`}
                        />
                      </ListItem>
                    ))
                  )}
                </CardContent>
              </Card>
              {/* work experiences */}
              <Card sx={{ mt: 1 }} ref={expRef}>
                <CardHeader
                  title="Work Experiences"
                  action={
                    <IconButton
                      onClick={() => setExpModal({ open: true, data: null })}
                    >
                      <AddIcon />
                    </IconButton>
                  }
                />
                <CardContent>
                  {data?.work_experiences?.length == 0 ? (
                    <Typography
                      variant="body2"
                      color="#aaa"
                      sx={{ fontStyle: "italic" }}
                    >
                      No work experience added
                    </Typography>
                  ) : (
                    data?.work_experiences?.map((it) => (
                      <ListItem
                        key={it.id}
                        secondaryAction={
                          <Stack direction={"row"}>
                            <IconButton
                              edge="end"
                              size="small"
                              onClick={() =>
                                setExpModal({
                                  open: true,
                                  data: it,
                                })
                              }
                            >
                              <EditOutlined fontSize="small" />
                            </IconButton>
                            <IconButton
                              edge="end"
                              size="small"
                              color="error"
                              onClick={() =>
                                setConfirmDeleteExpModal({
                                  open: true,
                                  id: it.id,
                                })
                              }
                            >
                              <DeleteOutline fontSize="small" />
                            </IconButton>
                          </Stack>
                        }
                      >
                        <ListItemIcon>
                          <SchoolOutlined />
                        </ListItemIcon>
                        <ListItemText
                          primary={`${it.company}`}
                          secondary={`${it.start_period_year || "?"} - ${
                            it.end_period_year || ""
                          }`}
                        />
                      </ListItem>
                    ))
                  )}
                </CardContent>
              </Card>
              {/* educations */}
              <Card sx={{ mt: 1 }} ref={eduRef}>
                <CardHeader
                  title="Educations"
                  action={
                    <IconButton
                      onClick={() => setEduModal({ open: true, data: null })}
                    >
                      <AddIcon />
                    </IconButton>
                  }
                />
                <CardContent>
                  {data?.educations?.length == 0 ? (
                    <Typography
                      variant="body2"
                      color="#aaa"
                      sx={{ fontStyle: "italic" }}
                    >
                      No education added
                    </Typography>
                  ) : (
                    data?.educations?.map((it) => (
                      <ListItem
                        key={it.id}
                        secondaryAction={
                          <Stack direction={"row"}>
                            <IconButton
                              edge="end"
                              size="small"
                              onClick={() =>
                                setEduModal({
                                  open: true,
                                  data: it,
                                })
                              }
                            >
                              <EditOutlined fontSize="small" />
                            </IconButton>
                            <IconButton
                              edge="end"
                              size="small"
                              color="error"
                              onClick={() =>
                                setConfirmDeleteEduModal({
                                  open: true,
                                  id: it.id,
                                })
                              }
                            >
                              <DeleteOutline fontSize="small" />
                            </IconButton>
                          </Stack>
                        }
                      >
                        <ListItemIcon>
                          <SchoolOutlined />
                        </ListItemIcon>
                        <ListItemText
                          primary={`${it.school}`}
                          secondary={`${it.field_of_study || ""}`}
                        />
                      </ListItem>
                    ))
                  )}
                </CardContent>
              </Card>
              {/* submit buttons */}
              <Stack direction="row" justifyContent={"flex-end"} sx={{ mt: 2 }}>
                <LoadingButton
                  type="submit"
                  variant="contained"
                  size="large"
                  loading={updating || updatingSkills || updatingLanguages}
                >
                  Update
                </LoadingButton>
              </Stack>
            </Box>
          </form>
        </Grid>
      </Grid>
      <ConfirmDeleteModal
        open={deleteCertModal.open}
        onClose={closeCertDeleteModal}
        onDelete={handleCertDelete}
        message={`Delete User Certification #${deleteCertModal.id} ?`}
      />
      <ConfirmDeleteModal
        open={deleteEduModal.open}
        onClose={closeEduDeleteModal}
        onDelete={handleEduDelete}
        message={`Delete User Education #${deleteEduModal.id} ?`}
      />
      <ConfirmDeleteModal
        open={deleteExpModal.open}
        onClose={closeExpDeleteModal}
        onDelete={handleExpDelete}
        message={`Delete User Work Experience #${deleteExpModal.id} ?`}
      />
      <ModalUserCertificationForm
        open={certModal.open}
        onClose={() => setCertModal({ open: false, data: null })}
        userId={user?.id!}
        data={certModal.data}
      />
      <ModalUserEducationForm
        open={eduModal.open}
        onClose={() => setEduModal({ open: false, data: null })}
        userId={user?.id!}
        data={eduModal.data}
      />
      <ModalUserExperienceForm
        open={expModal.open}
        onClose={() => setExpModal({ open: false, data: null })}
        userId={user?.id!}
        data={expModal.data}
      />
    </Box>
  );
};
export default EditAccountPage;
